import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

const UnauthorizedMessage = () => (
  <Box
    component={motion.div}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
      p: 3
    }}
  >
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 500,
        textAlign: 'center'
      }}
    >
      <Typography variant="h5" color="error" gutterBottom>
        Unauthorized Access
      </Typography>
      <Typography variant="body1" color="text.secondary">
        You don't have permission to access this agent. Please contact your administrator if you believe this is a mistake.
      </Typography>
    </Paper>
  </Box>
);

const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const ProtectedAgentConfigRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { id } = useParams(); // Get agent ID from URL params
  const [agentData, setAgentData] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(id ? true : false);
  const [error, setError] = useState(null);
  
  // Fetch agent data if we have an ID (editing existing agent)
  useEffect(() => {
    if (id) {
      const fetchAgentData = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/agent/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            }
          });
          
          if (!response.ok) {
            throw new Error('Failed to fetch agent data');
          }
          
          const data = await response.json();
          setAgentData(data);
        } catch (error) {
          console.error('Error fetching agent:', error);
          setError(error.message);
        } finally {
          setLoadingAgent(false);
        }
      };
      
      fetchAgentData();
    }
  }, [id]);
  
  if (loading || loadingAgent) {
    return <div className="loading">Loading...</div>;
  }

  // Check if user can configure agents
  const canConfigure = 
    user && 
    (user.role === 'admin' || 
     user.role === 'agency' || 
     (user.role === 'client' && user.canConfigureAgents));

  // If no user or user can't configure agents at all, redirect to login
  if (!user || !canConfigure) {
    return <Navigate to="/login" replace />;
  }

  // For existing agents (editing mode), check if agent is external or user has permission
  if (id && agentData) {
    // Check if agent is external
    const isExternal = agentData.is_external === true;
    
    // Check if user has permission to configure this specific agent
    const isAdmin = user.role === 'admin';
    const isAgentOwner = agentData.owner_id === user.id;
    const isAgency = user.role === 'agency';
    const isClientWithAccess = user.role === 'client' && agentData.client_configurable === true;
    
    const hasPermission = isAdmin || isAgentOwner || isAgency || isClientWithAccess;
    
    // If agent is external OR user doesn't have permission for this agent
    if (isExternal || !hasPermission) {
      return <Navigate to="/agents" replace />;
    }
  }

  // For existing agents (editing mode), check if agent is external or user has permission
  if (id && agentData) {
    // Check if agent is external
    const isExternal = agentData.is_external === true;
    
    // Check if user has permission to configure this specific agent
    const isAdmin = user.role === 'admin';
    const isAgentOwner = agentData.owner_id === user.id;
    const isAgency = user.role === 'agency';
    const isClientWithAccess = user.role === 'client' && agentData.client_configurable === true;
    
    const hasPermission = isAdmin || isAgentOwner || isAgency || isClientWithAccess;
    
    // If agent is external OR user doesn't have permission for this agent
    if (isExternal || !hasPermission) {
      return <Navigate to="/agents" replace />;
    }
  }

  // User has access, show the children components
  return children ? children : <Outlet />;
};

export default ProtectedAgentConfigRoute; 